import appConfig from "@config";
import { toast } from "sonner";
import { getLanguageLabel } from "../utils";

export const HYMNS_API_URL = appConfig.SUPABASE_REST_URL;
export const HYMNS_ANON_KEY = appConfig.SUPABASE_ANON_KEY;

/**
 * Centrally notify users of Supabase REST API failures gracefully
 */
export function notifyApiError(
  error: unknown,
  fallbackMessage = "Impossible de joindre le serveur de cantiques. Veuillez vérifier votre connexion ou réessayer plus tard.",
) {
  console.error("Supabase REST API error:", error);
  try {
    toast.error(fallbackMessage, {
      id: "supabase-api-error",
      duration: 5000,
    });
  } catch {
    // In environments where toast is unavailable
  }
}

export interface VerseItem {
  number?: number | null;
  order?: number;
  type?: "verse" | "chorus" | string;
  text?: string;
  content?: string;
  lines?: string[];
}

export interface Hymn {
  id: string;
  section: string;
  section_code: string;
  number: number;
  title: string;
  category?: string | null;
  language: string;
  verses?: VerseItem[] | string[] | any;
  full_text?: string;
  media_url?: string | null;
  source_data?: string | null;
}

export interface SectionSummary {
  section_code: string;
  related_section_code: string[];
  section: string;
  language?: string;
  total_hymns: number;
  is_section_group: boolean;
  order?: number;
}

export interface HymnSearchResult extends Hymn {
  rank?: number;
  snippet?: string;
}

/**
 * Dictionary defining if song media/audio is activated and verified for each hymn section.
 * Only sections set to `true` will retain their `media_url`; other sections will have `media_url` forced to null.
 */
export const SECTION_MEDIA_STATUS: Record<string, boolean> = {
  "CE-FR": false,
  "CE-CR": true,
  "MJ-FR": false,
  "MJ-CR": false,
  "RN-FR": false,
  "RN-CR": false,
  "RNC-CR": false,
  "VR-FR": false,
  "VR-CR": false,
  "HC-CR": false,
  EE: false,
  GA: false,
  OR: false,
  RD: false,
  TC: false,
  CRX: false,
  "CB-FR": false,
  "CJ-FR": false,
  "CJ-CR": false,
};

/**
 * Check if media/audio is activated for a given section code or section name.
 * In development mode, media is enabled for all sections for testing/verification.
 * In production mode, media is restricted to only verified sections in SECTION_MEDIA_STATUS (e.g. CE-CR: true).
 */
export function isSectionMediaActive(
  sectionCode?: string | null,
  sectionName?: string | null,
): boolean {
  return true; // DESACTIVATE FOR NOW SHOW ALL SONGS

  // In development mode, media is enabled for all sections for testing/verification
  if (!import.meta.env.PROD) {
    return true;
  }

  if (!sectionCode && !sectionName) {
    return false;
  }

  // 1. Direct and normalized lookup by sectionCode
  if (sectionCode) {
    const raw = sectionCode.trim().toUpperCase();
    if (SECTION_MEDIA_STATUS[raw] === true) {
      return true;
    }

    // Normalize underscores or whitespace to dashes (e.g. "CE_CR" or "CE CR" -> "CE-CR")
    const withDash = raw.replace(/[\s_]+/g, "-");
    if (SECTION_MEDIA_STATUS[withDash] === true) {
      return true;
    }

    // Check alphanumeric match (e.g. "CECR" -> "CE-CR")
    const cleanAlphaNum = raw.replace(/[^A-Z0-9]/g, "");
    for (const [key, isEnabled] of Object.entries(SECTION_MEDIA_STATUS)) {
      if (isEnabled && key.replace(/[^A-Z0-9]/g, "") === cleanAlphaNum) {
        return true;
      }
    }
  }

  // 2. Fallback check by sectionName if section_code wasn't provided or didn't match
  if (sectionName) {
    const rawName = sectionName.trim().toUpperCase();
    for (const [key, isEnabled] of Object.entries(SECTION_MEDIA_STATUS)) {
      if (isEnabled && rawName.includes(key)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Overrides media_url to null if media is not activated for the hymn's section
 */
export function sanitizeHymnMedia<
  T extends {
    section_code?: string | null;
    section?: string | null;
    media_url?: string | null;
  },
>(hymn: T): T {
  if (!hymn) return hymn;
  const isActive = isSectionMediaActive(hymn.section_code, hymn.section);
  return {
    ...hymn,
    media_url: isActive ? (hymn.media_url ?? null) : null,
  };
}

const defaultHeaders: HeadersInit = {
  apikey: HYMNS_ANON_KEY,
  Authorization: `Bearer ${HYMNS_ANON_KEY}`,
  "Content-Type": "application/json",
};

/**
 * Fetch sections catalog summary with total hymns and languages
 */
export async function getSectionsSummary(): Promise<SectionSummary[]> {
  try {
    const res = await fetch(
      `${HYMNS_API_URL}/sections?select=section_code,related_section_code,section,language,total_hymns,is_section_group,order&order=order.asc.nullslast,section_code.asc`,
      { headers: defaultHeaders },
    );
    if (!res.ok) {
      throw new Error(
        `Failed to fetch sections: ${res.statusText} (${res.status})`,
      );
    }
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    notifyApiError(
      error,
      "Impossible de charger les recueils de cantiques. Veuillez vérifier votre connexion.",
    );
    throw error;
  }
}

/**
 * Internal helper to fetch hymns for a single language from Supabase REST API
 */
async function fetchHymnsForSingleLanguage(
  sectionCode: string,
  languageCode?: string,
  limit = 600,
  offset = 0,
): Promise<Hymn[]> {
  const cleanCode = encodeURIComponent(sectionCode);
  const sectionFilter = sectionCode.includes("-")
    ? `section_code=eq.${cleanCode}`
    : `or=(section_code.eq.${cleanCode},section_code.ilike.${cleanCode}-*)`;

  let url = `${HYMNS_API_URL}/hymns?${sectionFilter}&select=id,section,section_code,number,title,category,language,verses,full_text,media_url,source_data&order=number.asc&limit=${limit}&offset=${offset}`;
  if (languageCode && languageCode !== "ALL" && languageCode !== "*") {
    url += `&language=eq.${encodeURIComponent(languageCode)}`;
  }
  const res = await fetch(url, { headers: defaultHeaders });
  if (!res.ok) {
    throw new Error(
      `Failed to fetch hymns for section ${sectionCode}: ${res.statusText} (${res.status})`,
    );
  }
  const data = await res.json();
  return Array.isArray(data) ? data.map(sanitizeHymnMedia) : [];
}

/**
 * Fetch all hymns belonging to a section_code ordered by hymn number.
 * Supports multiple languages passed as comma-separated string (e.g. "ht,fr") or string array.
 * When multiple languages are requested, queries are executed asynchronously in parallel (Promise.all)
 * and all results are combined and sorted by hymn number ascending.
 */
export async function getHymnsBySection(
  sectionCode: string,
  languageCode?: string | string[],
  limit = 600,
  offset = 0,
): Promise<Hymn[]> {
  try {
    const rawLangs = Array.isArray(languageCode)
      ? languageCode
      : languageCode
        ? languageCode.split(",").map((l) => l.trim()).filter(Boolean)
        : [];

    const langs = rawLangs.filter((l) => l !== "ALL" && l !== "*");

    if (langs.length > 1) {
      // Execute all language requests asynchronously in parallel (task.wait_all / Promise.all)
      const results = await Promise.all(
        langs.map((lang) =>
          fetchHymnsForSingleLanguage(sectionCode, lang, limit, offset),
        ),
      );

      // Flatten results
      const allHymns = results.flat();

      // Deduplicate by unique hymn id or composite key
      const seen = new Set<string>();
      const uniqueHymns: Hymn[] = [];
      for (const h of allHymns) {
        const key = h.id || `${h.section_code}-${h.number}-${h.language}`;
        if (!seen.has(key)) {
          seen.add(key);
          uniqueHymns.push(h);
        }
      }

      // Order all hymns by number ascending
      return uniqueHymns.sort((a, b) => {
        if (a.number !== b.number) return a.number - b.number;
        return (a.language || "").localeCompare(b.language || "");
      });
    }

    const singleLang = langs.length === 1 ? langs[0] : undefined;
    return await fetchHymnsForSingleLanguage(
      sectionCode,
      singleLang,
      limit,
      offset,
    );
  } catch (error) {
    notifyApiError(
      error,
      `Impossible de charger les cantiques du recueil ${sectionCode}. Veuillez vérifier votre connexion.`,
    );
    throw error;
  }
}

/**
 * Fetch a single hymn by section code and hymn number
 */
export async function getHymnBySectionAndNumber(
  sectionCode: string,
  number: number,
): Promise<Hymn | null> {
  try {
    const cleanCode = encodeURIComponent(sectionCode);
    const sectionFilter = sectionCode.includes("-")
      ? `section_code=eq.${cleanCode}`
      : `or=(section_code.eq.${cleanCode},section_code.ilike.${cleanCode}-*)`;
    const res = await fetch(
      `${HYMNS_API_URL}/hymns?${sectionFilter}&number=eq.${number}&select=id,section,section_code,number,title,category,language,verses,full_text,media_url,source_data&limit=1`,
      { headers: defaultHeaders },
    );
    if (!res.ok) {
      throw new Error(
        `Failed to fetch hymn ${sectionCode} N° ${number}: ${res.statusText} (${res.status})`,
      );
    }
    const data = await res.json();
    return Array.isArray(data) && data.length > 0
      ? sanitizeHymnMedia(data[0])
      : null;
  } catch (error) {
    notifyApiError(
      error,
      `Impossible de charger le cantique N° ${number} (${sectionCode}). Veuillez vérifier votre connexion.`,
    );
    throw error;
  }
}

/**
 * Fetch a single hymn by its UUID
 */
export async function getHymnById(id: string): Promise<Hymn | null> {
  try {
    const cleanId = encodeURIComponent(id);
    const res = await fetch(
      `${HYMNS_API_URL}/hymns?id=eq.${cleanId}&select=id,section,section_code,number,title,category,language,verses,full_text,media_url,source_data&limit=1`,
      { headers: defaultHeaders },
    );
    if (!res.ok) {
      throw new Error(
        `Failed to fetch hymn ${id}: ${res.statusText} (${res.status})`,
      );
    }
    const data = await res.json();
    return Array.isArray(data) && data.length > 0
      ? sanitizeHymnMedia(data[0])
      : null;
  } catch (error) {
    notifyApiError(
      error,
      "Impossible de charger le cantique demandé. Veuillez vérifier votre connexion.",
    );
    throw error;
  }
}

/**
 * Get community hymns for "Popular this week / Les cantiques que l'assemblée chante"
 * Uses /rpc/get_hymn_of_the_day?p_limit=${limit}
 */
export async function getPopularHymns(limit = 6): Promise<Hymn[]> {
  try {
    const res = await fetch(
      `${HYMNS_API_URL}/rpc/get_hymn_of_the_day?p_limit=${limit}`,
      { headers: defaultHeaders },
    );
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        return data.map(sanitizeHymnMedia);
      }
    }
  } catch (err) {
    console.warn("get_hymn_of_the_day failed, trying fallback hymns", err);
  }

  // Fallback: fetch top hymns from popular sections
  try {
    const fallbackRes = await fetch(
      `${HYMNS_API_URL}/hymns?section_code=eq.CE-CR&limit=${limit}&order=number.asc`,
      { headers: defaultHeaders },
    );
    if (fallbackRes.ok) {
      const data = await fallbackRes.json();
      return Array.isArray(data) ? data.map(sanitizeHymnMedia) : [];
    }
    throw new Error(`Fallback popular hymns failed: ${fallbackRes.status}`);
  } catch (err) {
    notifyApiError(
      err,
      "Impossible de charger les cantiques populaires. Veuillez vérifier votre connexion.",
    );
    throw err;
  }
}

/**
 * Predefined curated list of Top Cantiques
 */
export const TOP_HYMNS_KEYS: Array<{ section_code: string; number: number }> = [
  { section_code: "CE-CR", number: 54 },
  { section_code: "CE-CR", number: 114 },
  { section_code: "CE-CR", number: 35 },
  { section_code: "CE-CR", number: 33 },
  { section_code: "CE-CR", number: 28 },
  { section_code: "CE-CR", number: 24 },
  { section_code: "CE-FR", number: 34 },
  { section_code: "CE-CR", number: 1 },
  { section_code: "CE-CR", number: 51 },
  { section_code: "CE-FR", number: 137 },
  { section_code: "CE-FR", number: 60 },
  { section_code: "CE-FR", number: 52 },
  { section_code: "CE-FR", number: 54 },
  { section_code: "CE-FR", number: 11 },
  { section_code: "MJ-FR", number: 3 },
  { section_code: "CE-FR", number: 18 },
  { section_code: "CE-FR", number: 19 },
  { section_code: "CE-FR", number: 117 },
  { section_code: "CE-FR", number: 120 },
  { section_code: "CE-FR", number: 133 },
  { section_code: "CE-FR", number: 136 },
  { section_code: "CE-FR", number: 144 },
  { section_code: "CE-FR", number: 147 },
  { section_code: "CE-FR", number: 155 },
  { section_code: "CE-FR", number: 160 },
  { section_code: "CE-FR", number: 164 },
  { section_code: "CE-FR", number: 180 },
  { section_code: "CE-FR", number: 181 },
  { section_code: "CE-FR", number: 194 },
  { section_code: "CE-FR", number: 204 },
  { section_code: "CE-FR", number: 212 },
  { section_code: "CE-FR", number: 215 },
  { section_code: "CE-FR", number: 219 },
  { section_code: "CE-FR", number: 220 },
  { section_code: "CE-FR", number: 237 },
  { section_code: "CE-FR", number: 262 },
  { section_code: "CE-FR", number: 278 },
  { section_code: "CE-FR", number: 290 },
  { section_code: "CE-FR", number: 296 },
  { section_code: "CE-FR", number: 299 },
  { section_code: "CE-FR", number: 307 },
  { section_code: "CE-FR", number: 319 },
  { section_code: "CE-FR", number: 325 },
  { section_code: "CE-FR", number: 26 },
];

/**
 * Fetch top / most beloved hymns all at once using PostgREST 'or' condition:
 * GET /rest/v1/hymns?or=(and(section_code.eq.CE-FR,number.eq.54),and(section_code.eq.CE-CR,number.eq.114)) ...
 */
export async function getTopHymns(
  items: Array<{ section_code: string; number: number }> = TOP_HYMNS_KEYS,
): Promise<Hymn[]> {
  try {
    const orConditions = items
      .map(
        (item) =>
          `and(section_code.eq.${encodeURIComponent(item.section_code)},number.eq.${item.number})`,
      )
      .join(",");

    const url = `${HYMNS_API_URL}/hymns?or=(${orConditions})&select=id,section,section_code,number,title,category,language,verses,full_text,media_url,source_data&limit=${Math.max(items.length, 100)}`;
    const res = await fetch(url, { headers: defaultHeaders });

    if (!res.ok) {
      throw new Error(
        `Failed to fetch top hymns: ${res.statusText} (${res.status})`,
      );
    }

    const data = await res.json();
    if (!Array.isArray(data)) return [];

    // Map order by items array to keep the exact ranking
    const orderMap = new Map<string, number>();
    items.forEach((item, index) => {
      const key = `${item.section_code}_${item.number}`;
      if (!orderMap.has(key)) {
        orderMap.set(key, index);
      }
    });

    const hymns = data.map(sanitizeHymnMedia);
    hymns.sort((a, b) => {
      const rankA = orderMap.get(`${a.section_code}_${a.number}`) ?? 9999;
      const rankB = orderMap.get(`${b.section_code}_${b.number}`) ?? 9999;
      return rankA - rankB;
    });

    return hymns;
  } catch (error) {
    console.error("Failed to load top hymns:", error);
    notifyApiError(
      error,
      "Impossible de charger les top cantiques pour le moment.",
    );
    throw error;
  }
}

export interface GetRandomHymnsParams {
  /**
   * Filter by section code (query param: p_section_code)
   * Options: CE-FR, CE-CR, MJ-FR, MJ-CR, RN-FR, RN-CR, RNC-CR, VR-FR, VR-CR, HC-CR, EE, GA, OR, RD, TC, CRX, CB-FR, CJ-FR, CJ-CR
   */
  sectionCode?: string;
  /**
   * Filter by language (query param: p_language)
   * Options: ht (Haitian Creole), fr (French), en (English), es (Spanish)
   */
  language?: string;
  /**
   * Number of random hymns to fetch (query param: p_limit, default: 1)
   */
  limit?: number;
}

/**
 * Route: GET /rpc/get_random
 * Description: Get random hymn from the catalog, optionally filtered by section or language.
 *
 * @param paramsOrLimit Number of hymns or options object with sectionCode, language, limit
 * @param sectionCodeParam Optional section code filter if first param is limit number
 * @param languageParam Optional language filter if first param is limit number
 * @returns Promise<Hymn[]> Array of randomly selected hymns
 */
export async function getRandomHymns(
  paramsOrLimit: number | GetRandomHymnsParams = 4,
  sectionCodeParam?: string,
  languageParam?: string,
): Promise<Hymn[]> {
  let limit = 4;
  let sectionCode: string | undefined = sectionCodeParam;
  let language: string | undefined = languageParam;

  if (typeof paramsOrLimit === "number") {
    limit = paramsOrLimit;
  } else if (paramsOrLimit && typeof paramsOrLimit === "object") {
    limit = paramsOrLimit.limit ?? 1;
    sectionCode = paramsOrLimit.sectionCode;
    language = paramsOrLimit.language;
  }

  const queryParams = new URLSearchParams();
  queryParams.set("p_limit", String(limit));
  if (sectionCode && sectionCode !== "ALL" && sectionCode !== "*") {
    queryParams.set("p_section_code", sectionCode);
  }
  if (language && language !== "ALL" && language !== "*") {
    queryParams.set("p_language", language);
  }

  try {
    const res = await fetch(
      `${HYMNS_API_URL}/rpc/get_random?${queryParams.toString()}`,
      { headers: defaultHeaders },
    );
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        return data.map(sanitizeHymnMedia);
      }
    }
  } catch (err) {
    console.warn("get_random failed, falling back:", err);
  }

  try {
    let fallbackUrl = `${HYMNS_API_URL}/hymns?limit=${limit}&order=number.desc`;
    if (sectionCode && sectionCode !== "ALL" && sectionCode !== "*") {
      fallbackUrl += `&section_code=eq.${encodeURIComponent(sectionCode)}`;
    }
    if (language && language !== "ALL" && language !== "*") {
      fallbackUrl += `&language=eq.${encodeURIComponent(language)}`;
    }
    const fallbackRes = await fetch(fallbackUrl, { headers: defaultHeaders });
    if (fallbackRes.ok) {
      const data = await fallbackRes.json();
      return Array.isArray(data) ? data.map(sanitizeHymnMedia) : [];
    }
    throw new Error(`Fallback random hymns failed: ${fallbackRes.status}`);
  } catch (err) {
    notifyApiError(
      err,
      "Impossible de charger les suggestions de cantiques. Veuillez vérifier votre connexion.",
    );
    throw err;
  }
}

/**
 * Route: GET /rpc/get_random
 * Convenient helper to fetch a single random hymn.
 *
 * @param sectionCode Optional section code filter
 * @param language Optional language filter
 * @returns Promise<Hymn | null> A single random hymn or null
 */
export async function getRandomHymn(
  sectionCode?: string,
  language?: string,
): Promise<Hymn | null> {
  const hymns = await getRandomHymns({ limit: 1, sectionCode, language });
  return hymns.length > 0 ? hymns[0] : null;
}

/**
 * Advanced full-text search with p_limit
 */
export async function searchHymns(
  query: string,
  sectionCode?: string,
  limit = 15,
): Promise<HymnSearchResult[]> {
  if (!query || query.trim().length === 0) return [];

  try {
    const params = new URLSearchParams();
    params.set("p_search_query", query.trim());
    params.set("p_limit", String(limit));
    if (sectionCode && sectionCode !== "ALL" && sectionCode !== "*") {
      params.set("p_section_code", sectionCode);
    }

    const res = await fetch(
      `${HYMNS_API_URL}/rpc/search?${params.toString()}`,
      {
        headers: defaultHeaders,
      },
    );
    if (!res.ok) {
      return await searchHymnsFallback(query, sectionCode, limit);
    }
    const data = await res.json();
    return Array.isArray(data) ? data.map(sanitizeHymnMedia) : [];
  } catch (error) {
    console.warn("RPC search failed, falling back:", error);
    return await searchHymnsFallback(query, sectionCode, limit);
  }
}

async function searchHymnsFallback(
  query: string,
  sectionCode?: string,
  limit = 15,
): Promise<HymnSearchResult[]> {
  try {
    const cleanQ = encodeURIComponent(`*${query}*`);
    let url = `${HYMNS_API_URL}/hymns?title=ilike.${cleanQ}&limit=${limit}`;
    if (sectionCode && sectionCode !== "ALL" && sectionCode !== "*") {
      const cleanCode = encodeURIComponent(sectionCode);
      const sectionFilter = sectionCode.includes("-")
        ? `section_code=eq.${cleanCode}`
        : `or=(section_code.eq.${cleanCode},section_code.ilike.${cleanCode}-*)`;
      url += `&${sectionFilter}`;
    }
    const res = await fetch(url, { headers: defaultHeaders });
    if (!res.ok) {
      throw new Error(`Search fallback failed: ${res.status}`);
    }
    const data = await res.json();
    return Array.isArray(data) ? data.map(sanitizeHymnMedia) : [];
  } catch (err) {
    notifyApiError(
      err,
      "Erreur lors de la recherche des cantiques. Veuillez vérifier votre connexion.",
    );
    throw err;
  }
}

/**
 * Helper mapping to extract collection badge acronym (CE, GA, HC, etc.) and clean title
 */
export interface CollectionBadgeInfo {
  code: string;
  badge: string;
  name: string;
  languageName: string;
  languageCode: string;
  hymnTotal: number;
  isSectionGroup: boolean;
  relatedSectionCodes: string[];
  order?: number;
}

export function getCollectionBadgeInfo(
  summary: SectionSummary,
): CollectionBadgeInfo {
  const badge =
    summary.section_code.split("-")[0] ||
    summary.section_code.substring(0, 2).toUpperCase();

  const isSectionGroup = summary.is_section_group ?? false;

  // Use the full section title from API; if it's a section group,
  // remove language suffixes like (Français), (Kreyòl), etc.
  let name = summary.section || summary.section_code;
  if (isSectionGroup) {
    name = name.replace(/\s*\([^)]*\)\s*$/, "").trim();
  }

  const languageName = getLanguageLabel(summary.language);

  return {
    code: summary.section_code,
    badge,
    name,
    languageName,
    languageCode: summary.language || "",
    hymnTotal: summary.total_hymns,
    isSectionGroup,
    relatedSectionCodes: Array.isArray(summary.related_section_code)
      ? summary.related_section_code
      : [],
    order: summary.order,
  };
}
